import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of, combineLatest, firstValueFrom } from 'rxjs';
import { switchMap, catchError, tap, map, startWith, filter, take } from 'rxjs/operators';
import { WorkshopWork, Client, Bike, WorkStatus, WorkCurrentStatus, Employee, WorkItem } from '../core/models';
import { FirestoreDataService } from '../core/firestore-data.service';
import { AuthService } from '../core/auth.service';
import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';

declare var bootstrap: any;

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss']
})
export class HistoryComponent implements OnInit, AfterViewInit {

  @ViewChild('exportModal') exportModal!: ElementRef;
  bsExportModal: any;

  allCompletedWorks$: Observable<WorkshopWork[]>;
  filteredWorks$: Observable<WorkshopWork[]>;
  dateFilter$ = new BehaviorSubject<string>('all');
  availableMonths: { label: string, value: string }[] = [];

  // --- CONTROLES NUEVOS (Búsqueda y Exportación) ---
  workSearchControl = new FormControl('');
  exportStartDate = new FormControl('');
  exportEndDate = new FormControl('');

  selectedWorkId$ = new BehaviorSubject<string | null>(null);
  selectedWorkDetails$: Observable<WorkshopWork | null | undefined>;
  selectedClientDetails$: Observable<Client | null | undefined>;
  selectedBikeDetails$: Observable<Bike | null | undefined>;
  selectedWorkStatus$: Observable<WorkStatus | null>;
  
  // --- EMPLEADOS ---
  mechanics$: Observable<Employee[]>;
  assistants$: Observable<Employee[]>;
  allActiveEmployees$: Observable<Employee[]>;

  // --- ARRAY LOCAL PARA EDITAR ASIGNACIONES Y PRECIOS ---
  editableItems: any[] = [];
  pristineItems: any[] = []; // Para guardar el estado original de la BD y comparar precios

  isRevertingStatus = false;
  isSavingValidation = false;

  constructor(
    private firestoreDataService: FirestoreDataService,
    public authService: AuthService,
    private datePipe: DatePipe,
    private router: Router
  ) {
    this.dateFilter$.next(this.availableMonths[0]?.value || '');
    
    this.allCompletedWorks$ = this.firestoreDataService.completedWorks$;

    this.mechanics$ = this.firestoreDataService.getEmployees().pipe(
      map(employees => employees.filter(emp => emp.type === 'Mecánico' && emp.isActive))
    );
    this.assistants$ = this.firestoreDataService.getEmployees().pipe(
      map(employees => employees.filter(emp => emp.type === 'Asistente Taller' && emp.isActive))
    );
    
    this.allActiveEmployees$ = this.firestoreDataService.getEmployees().pipe(
      map(employees => employees.filter(emp => emp.isActive))
    );

    // --- FILTRO ACTUALIZADO: Combina Mes + Búsqueda por Texto ---
    this.filteredWorks$ = combineLatest([
      this.allCompletedWorks$,
      this.dateFilter$.pipe(startWith(this.availableMonths[0]?.value || '')),
      this.workSearchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([works, filterValue, searchTerm]) => {
        let result = works;

        // 1. Filtro por mes
        if (filterValue && filterValue !== 'all') {
          const [year, month] = filterValue.split('-').map(Number);
          result = result.filter(work => {
            const entryDate = work.entryDate?.toDate ? work.entryDate.toDate() : new Date(work.entryDate);
            return entryDate && entryDate.getFullYear() === year && (entryDate.getMonth() + 1) === month;
          });
        }

        // 2. Filtro de Búsqueda
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          result = result.filter(work => 
            work.clientName?.toLowerCase().includes(term) ||
            work.bikeDescription?.toLowerCase().includes(term) ||
            work.caseNumber?.toLowerCase().includes(term)
          );
        }

        return result;
      })
    );
    
    this.selectedWorkDetails$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkshopWorkById(id) : of(null)),
      tap(work => {
        if (work) {
          // Clonamos los artículos para que el manager pueda cambiarlos localmente
          this.editableItems = JSON.parse(JSON.stringify(work.addedItems || []));
          this.pristineItems = JSON.parse(JSON.stringify(work.addedItems || [])); // Copia intacta para comparar precios
          
          this.editableItems.forEach((item: any) => {
            if (this.isFullService(item)) {
              item._assignee1 = item.mechanicId || null;
              item._assignee2 = item.assistantId || null;
            } else {
              item._assignee1 = item.mechanicId || item.assistantId || null;
            }
          });
        } else {
          this.editableItems = [];
          this.pristineItems = [];
        }
      })
    );

    this.selectedClientDetails$ = this.selectedWorkDetails$.pipe(
      switchMap(work => work ? this.firestoreDataService.getClientById(work.clientId) : of(null))
    );
    
    this.selectedBikeDetails$ = this.selectedWorkDetails$.pipe(
      switchMap(work => work ? this.firestoreDataService.getBikeById(work.bikeId) : of(null))
    );

    this.selectedWorkStatus$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkStatus(id) : of(null))
    );
  }

  ngOnInit(): void {
    this.firestoreDataService.loadCompletedWorks();

    this.allCompletedWorks$.pipe(
      filter(works => works.length > 0),
      take(1) 
    ).subscribe(works => {
      this.generateDynamicMonthFilter(works);
    });
    
    setTimeout(() => {
      if (this.availableMonths.length === 0) {
        this.generateDefaultMonthFilter();
      }
    }, 2000);
  }

  ngAfterViewInit(): void {
    if (this.exportModal?.nativeElement) {
      this.bsExportModal = new bootstrap.Modal(this.exportModal.nativeElement);
    }
  }

  isFullService(item: any): boolean {
    const fullIds = ['234003', '234004', '234005'];
    return fullIds.includes(item.productId);
  }

  private generateDefaultMonthFilter(): void {
    this.availableMonths = [{ label: 'Todos los Meses', value: 'all' }];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${date.getMonth() + 1}`;
      let label = this.datePipe.transform(date, 'MMMM yyyy') || value;
      this.availableMonths.push({ label, value });
    }
  }

  private generateDynamicMonthFilter(works: WorkshopWork[]): void {
    this.availableMonths = [{ label: 'Todos los Meses', value: 'all' }];
    if (!works || works.length === 0) return;

    let oldestDate = new Date();
    works.forEach(work => {
      const workDate = work.entryDate?.toDate ? work.entryDate.toDate() : (work.entryDate instanceof Date ? work.entryDate : new Date());
      if (workDate < oldestDate) oldestDate = workDate;
    });

    const today = new Date();
    let currentLoopDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const stopDate = new Date(oldestDate.getFullYear(), oldestDate.getMonth(), 1);

    while (currentLoopDate >= stopDate) {
      const value = `${currentLoopDate.getFullYear()}-${currentLoopDate.getMonth() + 1}`;
      let label = '';
      try {
        label = this.datePipe.transform(currentLoopDate, 'MMMM yyyy', '', 'es') || '';
      } catch (e) {
        label = this.datePipe.transform(currentLoopDate, 'MMMM yyyy') || value;
      }
      if (label) label = label.charAt(0).toUpperCase() + label.slice(1);
      this.availableMonths.push({ label, value });
      currentLoopDate.setMonth(currentLoopDate.getMonth() - 1);
    }
  }

  onDateFilterChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.dateFilter$.next(selectElement.value);
  }

  selectWork(work: WorkshopWork): void {
    this.selectedWorkId$.next(work.id || null);
  }

  async revertToInProgress(workId: string): Promise<void> {
    const confirmation = window.confirm("¿Está seguro de que desea reabrir este trabajo? Volverá al estado 'En Progreso' en el panel de servicios activos.");
    if (confirmation) {
      this.isRevertingStatus = true;
      try {
        await this.firestoreDataService.reopenWork(workId);
        alert("¡Trabajo revertido a 'En Progreso' exitosamente!");
        this.selectedWorkId$.next(null);
      } catch (error: any) {
        alert(`Error al revertir el trabajo: ${error.message}`);
      } finally {
        this.isRevertingStatus = false;
      }
    }
  }

  async onValidateAndSave(workId: string, currentValidatedStatus: boolean | undefined): Promise<void> {
    const confirmation = window.confirm(
      currentValidatedStatus 
        ? "¿Deseas actualizar las asignaciones o precios para los artículos de este trabajo?"
        : "¿Confirma que desea validar este trabajo y guardar los cambios para las comisiones?"
    );

    if (confirmation) {
      this.isSavingValidation = true;
      try {
        const allEmps = await firstValueFrom(this.allActiveEmployees$.pipe(take(1)));

        this.editableItems.forEach((item, index) => {
          // 1. GESTIÓN DE PRECIOS
          item.unitPrice = Number(item.unitPrice);
          const pristineItem = this.pristineItems[index];

          if (item.unitPrice !== pristineItem.unitPrice) {
            if (item.originalPrice === undefined || item.originalPrice === null) {
              item.originalPrice = pristineItem.unitPrice;
            }
          }

          // 2. GESTIÓN DE ASIGNACIONES
          if (this.isFullService(item)) {
            const emp1 = allEmps.find(e => e.id === item._assignee1);
            const emp2 = allEmps.find(e => e.id === item._assignee2);

            if (emp1 && emp2 && emp1.type === emp2.type) {
              throw new Error(`En el servicio "${item.productName}", no puede asignar dos empleados del mismo rol. (Debe ser 1 Mecánico y 1 Asistente)`);
            }

            item.mechanicId = null; item.mechanicName = null;
            item.assistantId = null; item.assistantName = null;

            if (emp1) {
              if (emp1.type === 'Mecánico') { item.mechanicId = emp1.id; item.mechanicName = emp1.name; }
              else { item.assistantId = emp1.id; item.assistantName = emp1.name; }
            }
            if (emp2) {
              if (emp2.type === 'Mecánico') { item.mechanicId = emp2.id; item.mechanicName = emp2.name; }
              else { item.assistantId = emp2.id; item.assistantName = emp2.name; }
            }
          } else {
            const emp1 = allEmps.find(e => e.id === item._assignee1);
            
            item.mechanicId = null; item.mechanicName = null;
            item.assistantId = null; item.assistantName = null;

            if (emp1) {
              if (emp1.type === 'Mecánico') { item.mechanicId = emp1.id; item.mechanicName = emp1.name; }
              else { item.assistantId = emp1.id; item.assistantName = emp1.name; }
            }
          }

          delete item._assignee1;
          delete item._assignee2;
        });

        await this.firestoreDataService.validateAndAssignWorkItems(workId, this.editableItems);
        
        alert(currentValidatedStatus ? "Asignaciones y precios actualizados." : "Trabajo validado exitosamente.");
        this.selectedWorkId$.next(workId); // Recarga para ver cambios
      } catch (error: any) {
        alert(`Error al procesar: ${error.message}`);
      } finally {
        this.isSavingValidation = false;
      }
    }
  }

  // --- FUNCIONALIDAD 2: EXPORTAR A CSV ---
  openExportModal() {
    this.exportStartDate.setValue('');
    this.exportEndDate.setValue('');
    if(this.bsExportModal) this.bsExportModal.show();
  }

  async exportToCSV() {
    const start = this.exportStartDate.value;
    const end = this.exportEndDate.value;
    if(!start || !end) {
      alert("Por favor seleccione ambas fechas.");
      return;
    }

    const startDate = new Date(start);
    startDate.setHours(0,0,0,0);
    const endDate = new Date(end);
    endDate.setHours(23,59,59,999);

    const allWorks = await firstValueFrom(this.allCompletedWorks$.pipe(take(1)));
    const worksToExport = allWorks.filter(w => {
      const d = w.entryDate?.toDate ? w.entryDate.toDate() : new Date(w.entryDate);
      return d >= startDate && d <= endDate;
    });

    if(worksToExport.length === 0) {
      alert("No se encontraron órdenes completadas en este rango de fechas.");
      return;
    }

    const csvRows = [];
    csvRows.push(['N° Caso', 'Cliente', 'Bicicleta', 'Fecha Recepción', 'Total (RD$)', 'Estado Final', 'Validado'].join(','));
    
    worksToExport.forEach(w => {
      const dateStr = w.entryDate?.toDate ? w.entryDate.toDate().toLocaleDateString('es-DO') : '';
      csvRows.push([
        `"${w.caseNumber}"`,
        `"${w.clientName}"`,
        `"${w.bikeDescription}"`,
        `"${dateStr}"`,
        w.totalPrice || 0,
        `"${this.getSpanishStatus(w.currentStatus)}"`,
        w.validatedByManager ? 'Sí' : 'No'
      ].join(','));
    });

    const csvString = "\uFEFF" + csvRows.join('\n'); // BOM para UTF-8 en Excel
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Historial_Ordenes_${start}_al_${end}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (this.bsExportModal) this.bsExportModal.hide();
  }

  // --- FUNCIONALIDAD 3: IMPRIMIR ORDEN ---
  async printWork(work: WorkshopWork, client: Client, bike: Bike) {
    const statusDoc = await firstValueFrom(this.selectedWorkStatus$.pipe(take(1)));
    
    const printWindow = window.open('', '_blank', 'height=800,width=800');
    if (!printWindow) {
      alert("Su navegador bloqueó la ventana. Por favor permita las ventanas emergentes.");
      return;
    }

    printWindow.document.write('<html><head><title>Detalles de Orden - Historial</title>');
    printWindow.document.write(`<style>
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .section-title { font-size: 16px; font-weight: bold; background: #eee; padding: 8px; margin-top: 25px; margin-bottom: 10px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f8f9fa; }
        .text-right { text-align: right; }
        .status-list { list-style-type: none; padding-left: 0; }
        .status-list li { padding: 8px 0; border-bottom: 1px dashed #eee; font-size: 14px; }
    </style>`);
    printWindow.document.write('</head><body>');
    
    // Cabecera
    printWindow.document.write(`<div class="header">
      <h2>Reporte de Historial - Orden: ${work.caseNumber}</h2>
      <p style="margin:0; color:#666;">Emitido el: ${new Date().toLocaleString('es-DO')}</p>
    </div>`);
    
    // Info del Cliente y Bici
    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Cliente:</strong> ${client.name}<br><strong>Teléfono:</strong> ${client.phone || 'N/A'}</div>`);
    printWindow.document.write(`<div><strong>Fecha de Recepción:</strong> ${this.datePipe.transform(work.entryDate?.toDate ? work.entryDate.toDate() : work.entryDate, 'dd/MM/yyyy HH:mm', 'es')}<br><strong>Estado Final:</strong> ${this.getSpanishStatus(work.currentStatus)}</div>`);
    printWindow.document.write('</div>');

    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Bicicleta:</strong> ${bike.brand} ${bike.model || ''} - ${bike.color}<br><strong>Chasis:</strong> ${bike.chassis || 'N/A'}</div>`);
    printWindow.document.write(`<div><h3 style="margin:0;">Total: RD$ ${work.totalPrice}</h3></div>`);
    printWindow.document.write('</div>');

    // Artículos traídos por el cliente
    if (work.clientItems && work.clientItems.length > 0) {
      printWindow.document.write('<div class="section-title">Artículos / Piezas Traídas por el Cliente (Devueltos)</div>');
      printWindow.document.write('<ul style="margin: 5px 0; padding-left: 20px;">');
      work.clientItems.forEach(ci => {
        printWindow.document.write(`<li><strong>${ci}</strong></li>`);
      });
      printWindow.document.write('</ul>');
    }

    // Artículos
    printWindow.document.write('<div class="section-title">Artículos y Servicios Añadidos</div>');
    if (work.addedItems && work.addedItems.length > 0) {
        printWindow.document.write('<table><thead><tr><th>Descripción</th><th>Asignación</th><th>Cant.</th><th class="text-right">Precio Unit.</th><th class="text-right">Subtotal</th></tr></thead><tbody>');
        work.addedItems.forEach(item => {
            const subtotal = item.quantity * item.unitPrice;
            const assignment = (item.mechanicName || item.assistantName) 
              ? `${item.mechanicName ? 'Mec:'+item.mechanicName : ''} ${item.assistantName ? 'Asis:'+item.assistantName : ''}` 
              : 'N/A';
            printWindow.document.write(`<tr>
              <td>${item.productName}</td>
              <td style="font-size:12px; color:#555;">${assignment}</td>
              <td>${item.quantity}</td>
              <td class="text-right">RD$ ${item.unitPrice}</td>
              <td class="text-right">RD$ ${subtotal}</td>
            </tr>`);
        });
        printWindow.document.write('</tbody></table>');
    } else {
        printWindow.document.write('<p>No hay artículos registrados en esta orden.</p>');
    }

    // Historial
    printWindow.document.write('<div class="section-title">Historial de Tiempos y Estados</div>');
    if (statusDoc && statusDoc.history && statusDoc.history.length > 0) {
        printWindow.document.write('<ul class="status-list">');
        statusDoc.history.forEach(h => {
            const dStr = this.datePipe.transform(h.timestamp?.toDate ? h.timestamp.toDate() : h.timestamp, 'dd/MM/yyyy HH:mm', 'es');
            printWindow.document.write(`<li><strong>${dStr}</strong> - ${this.getSpanishStatus(h.status)} ${h.notes ? `<i>(Notas: ${h.notes})</i>` : ''}</li>`);
        });
        printWindow.document.write('</ul>');
    } else {
        printWindow.document.write('<p>No hay registro de historial detallado.</p>');
    }

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  }

  getSpanishStatus(statusValue: WorkCurrentStatus | undefined): string {
    if (!statusValue) return 'Desconocido';
    const statusMap: Record<WorkCurrentStatus, string> = {
      'Received': 'Recibido', 'In Progress': 'En Progreso', 'Awaiting Parts': 'Esperando Repuestos',
      'Ready': 'Lista', 'Completed': 'Completado', 'Handed Off': 'Entregada',  'Cancelled': 'Cancelado/Desestimado'
    };
    return statusMap[statusValue] || statusValue;
  }

    getStatusClass(status: WorkCurrentStatus | undefined): string {
    if (!status) return 'bg-secondary';
    switch (status) {
      case 'Received': return 'bg-primary';
      case 'In Progress': return 'bg-info text-dark';
      case 'Awaiting Parts': return 'bg-warning text-dark';
      case 'Ready': return 'bg-success';
      case 'Completed': return 'bg-dark';
      case 'Cancelled': return 'bg-danger';
      default: return 'bg-light text-dark';
    }
  }
}